import { CaretDown } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cn } from "~/foundation/utils/cn";

export interface SidebarSectionProps {
  title?: string;
  collapsed: boolean;
  onToggle: () => void;
  /** A small control at the end of the header (new / view all) — shown on hover or focus. */
  action?: ReactNode;
  children: ReactNode;
}

export function SidebarSection({
  title,
  collapsed,
  onToggle,
  action,
  children,
}: SidebarSectionProps) {
  if (!title) {
    return <div className="flex flex-col gap-0.5">{children}</div>;
  }

  return (
    <div className="flex flex-col gap-0.5">
      <div className="group/section flex items-center">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex h-7 min-w-0 flex-1 items-center gap-1 rounded-sm px-2 text-[11px] font-medium uppercase tracking-wide text-text-tertiary hover:bg-hover-bg"
        >
          <CaretDown
            size={10}
            aria-hidden
            className={cn(
              "transition-transform duration-100 motion-reduce:transition-none",
              collapsed && "-rotate-90 rtl:rotate-90",
            )}
          />
          <span className="truncate">{title}</span>
        </button>
        {action ? (
          <div className="flex shrink-0 items-center opacity-0 transition-opacity group-focus-within/section:opacity-100 group-hover/section:opacity-100 pointer-coarse:opacity-100 motion-reduce:transition-none">
            {action}
          </div>
        ) : null}
      </div>
      {collapsed ? null : <div className="flex flex-col gap-0.5">{children}</div>}
    </div>
  );
}

/** The header action's look — one icon button, sized to the 28px header row. */
export const SECTION_ACTION_CLASS =
  "flex h-6 w-6 items-center justify-center rounded-sm text-text-tertiary hover:bg-hover-bg hover:text-text";
